import { useEffect, useState, type FormEvent } from 'react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../components/CustomButton';
import SmallLoadingSpinner from '../../components/common/ui/SmallLoadingSpinner';
import { createBookingPaymentIntent } from '../../../service/paymentService';
import { formatCurrency } from '../../../utils/currency';

/**
 * Online booking payment via Stripe PaymentElement.
 *
 * Card data is collected ONLY by Stripe's hosted PaymentElement iframe — this
 * component never creates card inputs and never touches card details. The FE
 * obtains a PaymentIntent clientSecret from gigmosaic-payment-service
 * (POST /payment/api/v1/payments/pay), confirms it (3DS handled via redirect),
 * and reports the authorized status up to BookingPage, which polls booking
 * status until the backend capture confirms it.
 */

// Exported so BookingPage can hide the online option when Stripe isn't
// configured — must stay a publishable key only, never a secret.
export const STRIPE_PUBLISHABLE_KEY = (
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
).trim();

/** Identifiers persisted so the flow can resume after a 3DS redirect. */
export const PAYMENT_RESUME_KEY = 'booking.paymentResume';

/** Stripe returns the customer here after a 3DS challenge. */
const RETURN_URL = `${window.location.origin}/service/booking`;

/** Terminal PaymentIntent statuses that mean the payment is authorized. */
const AUTHORIZED_STATUSES = new Set([
  'requires_capture',
  'succeeded',
  'processing',
]);

// Module-scope promise: loadStripe initializes Stripe.js once for the app
// lifetime (documented pattern — never inside a component body).
const stripePromise = STRIPE_PUBLISHABLE_KEY
  ? loadStripe(STRIPE_PUBLISHABLE_KEY)
  : null;

const elementsAppearance = {
  theme: 'stripe' as const,
  variables: {
    // Brand palette — mirrors --primary (#317690) / --accent (#12bbb5).
    colorPrimary: '#317690',
    colorAccent: '#12bbb5',
    colorText: '#1f2937',
    colorDanger: '#dc2626',
    borderRadius: '8px',
    fontFamily: 'Outfit, sans-serif',
  },
};

interface CheckoutFormProps {
  onAuthorized: (paymentIntentStatus: string) => void;
  amount?: number; // Total amount in dollars (used for button label only, UX purposes)
  /** Stripe 3DS redirect target — defaults to the legacy /service/booking. */
  returnUrl?: string;
}

const CheckoutForm = ({
  onAuthorized,
  amount,
  returnUrl,
}: CheckoutFormProps) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isPaying, setIsPaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Format amount for display in button label (e.g., "Pay CAD 50.00")
  const formatAmountLabel = () => {
    if (!amount || amount <= 0) {
      return t('booking.pay_now');
    }
    return `Pay ${formatCurrency(amount)}`;
  };

  const handleSubmit = async (event?: FormEvent<HTMLFormElement>) => {
    // The event is optional: the Pay Now button calls this handler directly
    // (onPress) — see the button below — so no submit event is ever dispatched
    // into the nested-form structure. When the handler IS reached via a real
    // submit (Enter key, fireEvent.submit), stopPropagation is critical:
    // this <form> is nested inside BookingPage's react-hook-form <Form>
    // (invalid HTML, but browsers tolerate it), and a submit event bubbling up
    // to that outer form would re-run the whole booking onSubmit (double
    // booking) via its handleSubmit(BookingPage.onSubmit)(event) machinery.
    event?.preventDefault();
    event?.stopPropagation();
    if (!stripe || !elements) {
      setErrorMessage(t('booking.payment_not_configured'));
      return;
    }

    setIsPaying(true);
    setErrorMessage('');

    try {
      // 3DS/SCA: redirect only when the bank requires it. The booking flow
      // resumes via the `payment_intent_*` URL params + PAYMENT_RESUME_KEY.
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: { return_url: returnUrl || RETURN_URL },
      });

      if (error) {
        // card_declined, insufficient_funds, 3DS cancel, …
        setErrorMessage(error.message || t('booking.payment_failed'));
        return;
      }

      if (!paymentIntent || !AUTHORIZED_STATUSES.has(paymentIntent.status)) {
        // e.g. requires_payment_method without an error object.
        setErrorMessage(t('booking.payment_failed'));
        return;
      }

      // Authorized (requires_capture). BookingPage takes over from here:
      // shows "Payment authorized, confirming booking" and polls booking status.
      onAuthorized(paymentIntent.status);
    } catch (err: any) {
      setErrorMessage(err?.message || t('booking.payment_failed'));
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <PaymentElement options={{ layout: 'tabs' }} />

      {errorMessage && (
        <p className="text-xs text-red-500" role="alert">
          {errorMessage}
        </p>
      )}

      {/* type="button" + direct handler call — NEVER a native submit. This
          form is nested inside BookingPage's react-hook-form <Form>; a
          type="submit" button lets the click's submit event bubble into the
          outer form, whose handleSubmit can re-run booking creation and/or
          trigger default navigation (the booking form reloading on Pay Now). */}
      <CustomButton
        label={isPaying ? t('booking.payment_processing') : formatAmountLabel()}
        className="w-full"
        color="primary"
        size="md"
        type="button"
        onPress={() => void handleSubmit()}
        isLoading={isPaying}
        isDisabled={!stripe || !elements || isPaying}
      />
      <p className="text-[10px] text-gray-400 text-center">
        {t('booking.secure_payment_note')}
      </p>
    </form>
  );
};

interface PaymentSectionProps {
  /**
   * Payment doc type the payment service routes on — 'booking' (default) or
   * 'package'. Determines which server-side record `bookingId` resolves
   * against (Booking vs CustomerPackage) when deriving the charge amount.
   */
  paymentType?: string;
  /**
   * Reference id sent as the payment's `referenceId` — a bookingId (BID) for
   * paymentType 'booking', or a customerPackageId (CPK) for 'package'. The
   * payment service fetches the matching record server-side via this id and
   * derives the charge amount from it (never from the browser).
   */
  bookingId: string;
  /** Booking service, stored as PaymentIntent metadata for downstream events. */
  serviceId?: string;
  /** Booking provider, stored as PaymentIntent metadata for downstream events. */
  providerId?: string;
  /** Booked time slot, stored as PaymentIntent metadata for downstream events. */
  timeSlotId?: string;
  /** Called once the PaymentIntent is authorized (requires_capture). */
  onAuthorized: (paymentIntentStatus: string) => void;
  /** Total amount in dollars (used for button label only, UX purposes). */
  amount?: number;
  /**
   * Stripe 3DS redirect target after a challenge. Optional so callers on the
   * public pay page can return to `/booking/pay/<referenceCode>` and resume;
   * defaults to the legacy hardcoded /service/booking.
   */
  returnUrl?: string;
}

const PaymentSection = ({
  paymentType = 'booking',
  bookingId,
  serviceId,
  providerId,
  timeSlotId,
  onAuthorized,
  amount,
  returnUrl,
}: PaymentSectionProps) => {
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState('');
  const [isAcquiring, setIsAcquiring] = useState(false);
  const [acquireError, setAcquireError] = useState('');
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!STRIPE_PUBLISHABLE_KEY) {
      setAcquireError(t('booking.payment_not_configured'));
      return;
    }

    let cancelled = false;
    setIsAcquiring(true);
    setAcquireError('');
    setClientSecret('');

    // The amount is NOT sent from here — the payment service fetches the
    // booking server-side via referenceId (bookingId) and derives the charge
    // from the booking record (the browser must never state how much to
    // charge). Domain IDs ride along in metadata so Stripe attaches them to
    // all resulting payment events. Empty values are dropped — Stripe rejects
    // undefined, and '' would land in PI metadata untouched (the payment
    // service only overrides snake_case server-derived keys).
    const metadata: Record<string, string> = { bookingId };
    if (serviceId) metadata.serviceId = serviceId;
    if (providerId) metadata.providerId = providerId;
    if (timeSlotId) metadata.timeSlotId = timeSlotId;

    createBookingPaymentIntent({
      paymentType,
      referenceId: bookingId,
      description: `${paymentType === 'package' ? 'Package' : 'Booking'} ${bookingId}`,
      metadata,
    })
      .then(({ clientSecret: cs }) => {
        if (cancelled) return;
        setClientSecret(cs);
        // Persist ONLY identifiers (never card data) so a 3DS redirect can
        // resume the flow on page reload. Key name must match the BookingPage
        // reader ({ bookingId, referenceCode }).
        try {
          sessionStorage.setItem(
            PAYMENT_RESUME_KEY,
            JSON.stringify({ bookingId, referenceCode: bookingId })
          );
        } catch {
          /* sessionStorage unavailable — the redirect can't resume */
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setAcquireError(err?.message || t('booking.payment_failed'));
      })
      .finally(() => {
        if (!cancelled) setIsAcquiring(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentType, bookingId, serviceId, providerId, timeSlotId, retryNonce]);

  const handleAuthorized = (status: string) => {
    try {
      sessionStorage.removeItem(PAYMENT_RESUME_KEY);
    } catch {
      /* noop */
    }
    onAuthorized(status);
  };

  if (acquireError) {
    return (
      <div className="flex flex-col gap-2 items-center py-2">
        <p className="text-xs text-red-500 text-center" role="alert">
          {acquireError}
        </p>
        <CustomButton
          label={t('booking.payment_retry')}
          color="primary"
          size="sm"
          type="button"
          onPress={() => setRetryNonce((n) => n + 1)}
        />
      </div>
    );
  }

  if (isAcquiring || !clientSecret) {
    return (
      <div className="w-full flex justify-center items-center py-4">
        <SmallLoadingSpinner size="sm" />
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret, appearance: elementsAppearance }}
    >
      <CheckoutForm
        onAuthorized={handleAuthorized}
        amount={amount}
        returnUrl={returnUrl}
      />
    </Elements>
  );
};

export default PaymentSection;
